import type { StringKey } from "./fa";

/**
 * English (en). Typed against `StringKey`, so it must define every key Dari
 * defines — a missing key fails the build.
 */
export const en: Record<StringKey, string> = {
  // common
  common_menu: "Menu",
  common_close: "Close",
  common_cancel: "Cancel",
  common_language: "Language",

  // app shell
  app_name: "Talar",
  app_initial: "T",

  // nav
  nav_group_ops: "Operations",
  nav_group_finance: "Finance & admin",
  nav_group_market: "Marketplace",
  nav_group_platform: "Platform",
  nav_dashboard: "Dashboard",
  nav_calendar: "Booking calendar",
  nav_bookings: "Bookings",
  nav_contracts: "Contracts",
  nav_crm: "Customers",
  nav_finance: "Finance",
  nav_payouts: "Payouts",
  nav_hr: "Staff",
  nav_halls: "Halls",
  nav_reviews: "Reviews",
  nav_admin: "Platform admin",
  nav_change_password: "Change password",
  nav_logout: "Sign out",
  layout_panel_fallback: "Management panel",
  layout_today: "Today: {date}",

  // login
  login_subtitle: "Wedding-hall management panel",
  login_email: "Email",
  login_password: "Password",
  login_submit: "Sign in",
  login_submitting: "Signing in…",
  login_failed: "Sign-in failed",

  // onboarding
  onboarding_welcome: "Welcome",
  onboarding_intro:
    "To get started, register your business or hall name. After this step the full panel (bookings, calendar, finance, staff, customers…) opens for you.",
  onboarding_name_label: "Organization / hall name",
  onboarding_name_placeholder: "e.g. Ariana Halls Company",
  onboarding_submit: "Create organization and enter panel",
  onboarding_submitting: "Creating…",
  onboarding_error: "Could not create the organization",

  // route guard
  guard_no_access_title: "No access",
  guard_no_access_body:
    "This section is for the “{roles}” role. Contact the hall owner for access.",

  // dashboard
  dashboard_title: "Dashboard",
  dashboard_kpi_confirmed: "Confirmed bookings",
  dashboard_kpi_pending_requests: "Pending requests",
  dashboard_kpi_pending_requests_hint: "Need your approval",
  dashboard_kpi_pending_deposits: "Awaiting deposit",
  dashboard_kpi_upcoming: "Events in the next 7 days",
  dashboard_insights_title: "Hall insights",

  // change password
  cp_title: "Change password",
  cp_intro: "For security, enter your current password first.",
  cp_current_label: "Current password",
  cp_new_label: "New password",
  cp_repeat_label: "Repeat new password",
  cp_min_helper: "At least {n} characters",
  cp_submit: "Change password",
  cp_submitting: "Changing…",
  cp_success: "Password changed. Sign in with the new password next time.",
  cp_min_length: "New password must be at least {n} characters",
  cp_mismatch: "New password and its repeat do not match",
  cp_same_as_current: "New password is the same as the current one",
  cp_err_no_email: "This account did not sign in with email — password cannot be changed here",
  cp_err_wrong_password: "Current password is incorrect",
  cp_err_weak: "New password is too simple — mix letters and digits",
  cp_err_too_many: "Too many attempts — wait a few minutes and try again",
  cp_err_recent_login: "Your session is stale — sign out once and sign back in",
  cp_err_network: "No connection — check your internet",
  cp_err_generic: "Password change failed — try again",

  // shared — actions, columns, enums
  common_error: "Error",
  common_send: "Send",
  common_empty: "Nothing here",
  common_status: "Status",
  shift_morning: "Morning",
  shift_evening: "Evening",
  shift_full_day: "Full day",
  booking_status_pending_request: "Awaiting approval",
  booking_status_pending_deposit: "Awaiting deposit",
  booking_status_confirmed: "Confirmed",
  booking_status_completed: "Completed",
  booking_status_cancelled: "Cancelled",

  // bookings
  bookings_title: "Bookings",
  booking_tab_requests: "Requests",
  booking_tab_pending_deposit: "Awaiting deposit",
  booking_tab_confirmed: "Confirmed",
  booking_tab_completed: "Completed",
  bookings_col_date: "Date",
  bookings_col_shift: "Time",
  bookings_col_guests: "Guests",
  bookings_col_total: "Total",
  bookings_col_paid: "Paid",
  bookings_col_balance: "Balance",
  bookings_confirm: "Approve",
  bookings_contract: "Contract",
  bookings_issue_contract: "Issue contract",
  bookings_toast_confirmed: "Request approved — the customer was notified to pay the deposit",
  bookings_toast_contract_issued: "Contract issued — the customer was notified to sign",
  bookings_toast_receipt:
    "Receipt {no} recorded — verify it on the finance page to post it to the ledger",

  // receipt dialog
  receipt_title: "Record a receipt",
  receipt_submit: "Record receipt",
  receipt_balance: "Balance on this booking: {amount}",
  receipt_amount_label: "Amount (AFN)",
  receipt_method_label: "Payment method",
  receipt_method_cash: "Cash (till)",
  receipt_method_bank: "Bank transfer",
  receipt_note_label: "Note (optional)",
  receipt_failed: "Could not record the receipt",

  // calendar
  calendar_day_tooltip: "{count} guests · {status}",

  // chats
  chats_title: "Chat with customers",
  chats_empty: "No conversations yet",
  chats_reply_placeholder: "Write a reply…",

  // reviews
  reviews_title: "Customer reviews",
  reviews_your_reply: "Your reply",
  reviews_reply_placeholder: "Write your reply…",
  reviews_empty: "No published reviews yet",

  // contracts (page)
  contracts_title: "Contracts",
  contracts_subtitle:
    "Sealed digital contracts — issued from the bookings page, signed by the customer or in person at the hall",
  contracts_all: "All",
  contracts_awaiting_signature: "Awaiting signature",
  contracts_signed: "Signed",
  contracts_status_signed: "Signed ✓",
  contracts_stat_total: "Total contracts",
  contracts_stat_last_n: "Last {n}",
  contracts_hint_unsigned: "Customer has not signed yet",
  contracts_col_issued: "Issued on",
  contracts_col_signed_date: "Signed on",
  contracts_col_template: "Template",
  contracts_col_seal: "Digital seal",
  contracts_template_version: "Version {v}",
  contracts_view_print: "View & print",
  contracts_empty_none:
    "No contracts issued yet — issue one from the bookings page for a confirmed booking",
  contracts_empty_filtered: "Nothing in this state",

  // contract dialog
  contract_dialog_title: "Digital contract",
  contract_awaiting_customer: "Awaiting customer signature",
  contract_seal_line: "Digital seal: {hash}",
  contract_inperson_note:
    "If the customer signed the contract in person at the hall, record it here to finalize it.",
  contract_print_pdf: "Print / save PDF",
  contract_signing: "Recording…",
  contract_sign_inperson: "Record customer's in-person signature",
  contract_sign_failed: "Could not record the signature",

  // finance
  finance_title: "Finance — income & expenses",
  finance_net_profit: "Net profit",
  finance_total_income: "Total income",
  finance_total_expenses: "Total expenses",
  finance_pending_receipts: "Receipts awaiting verification",
  finance_col_receipt: "Receipt",
  finance_col_method: "Method",
  finance_col_amount: "Amount",
  finance_col_note: "Note",
  finance_method_cash: "Cash",
  finance_method_bank: "Bank",
  finance_verify_post: "Verify & post to ledger",
  finance_payouts_link: "Platform payouts (online payments) →",
  finance_coa_title: "Accounts (Chart of Accounts)",
  finance_col_code: "Code",
  finance_col_account: "Account",
  finance_col_type: "Type",
  finance_col_balance: "Balance",

  // payouts
  payouts_title: "Platform payouts",
  payouts_subtitle:
    "Customers' online payments first go to the platform account; after deducting commission, the platform transfers the net to the hall's bank account",
  payouts_refresh: "Refresh",
  payouts_stat_pending: "Pending payment to you",
  payouts_stat_paid: "Paid so far",
  payouts_stat_commission: "Platform commission",
  payouts_stat_commission_hint: "Across all periods",
  payouts_col_period: "Settlement period to",
  payouts_col_count: "Payments",
  payouts_col_gross: "Gross",
  payouts_col_commission: "Commission",
  payouts_col_net: "Net payable",
  payouts_col_paid_date: "Paid on",
  payouts_status_paid: "Paid",
  payouts_status_pending: "Pending",
  payouts_commission_cell: "{amount} ({pct}%)",
  payouts_empty:
    "No payouts yet — a payout is created when there is a verified, unsettled online payment",
  payouts_loading: "Loading…",
  payouts_footnote:
    "Deposits and cash payments you take at the hall are not in this table — that money is yours from the start and is recorded on the “Finance” page.",
  payouts_load_failed: "Could not fetch payouts",

  // shared (more)
  common_submit: "Save",
  common_note_optional: "Note (optional)",

  // crm
  crm_title: "Leads",
  crm_new_lead: "New lead",
  crm_stage_new: "New",
  crm_stage_contacted: "Contacted",
  crm_stage_visited: "Visited",
  crm_stage_quoted: "Quoted",
  crm_stage_won: "Won",
  crm_stage_lost: "Lost",
  crm_source_walk_in: "Walk-in",
  crm_source_phone: "Phone",
  crm_source_instagram: "Instagram",
  crm_source_referral: "Referral",
  crm_source_other: "Other",
  crm_guest_estimate_chip: "~{count} guests",
  crm_event_approx: "Approx. {date}",
  crm_advance_next: "{label} →",
  crm_toast_lead_saved: "Lead saved",
  crm_dialog_title: "New lead",
  crm_name: "Name",
  crm_phone: "Phone number",
  crm_source_label: "Source",
  crm_guests_label: "Estimated guests (optional)",

  // hr
  hr_title: "Staff & payroll",
  hr_new_employee: "New employee",
  hr_create_payroll: "Create {period} payroll",
  hr_col_name: "Name",
  hr_col_role: "Role",
  hr_col_salary: "Monthly salary",
  hr_col_today_attendance: "Today's attendance",
  hr_check_inout: "Check in/out",
  hr_empty_employees: "No employees yet",
  hr_payroll_runs_title: "Payroll runs",
  hr_col_period: "Period (Jalali)",
  hr_col_employees: "Employees",
  hr_col_net_total: "Net total",
  hr_run_status_posted: "Paid",
  hr_run_status_draft: "Draft",
  hr_post_run: "Pay & post to ledger",
  hr_empty_runs: "No payroll runs created",
  hr_dialog_title: "New employee",
  hr_full_name: "Full name",
  hr_salary_label: "Monthly salary (AFN)",
  hr_toast_employee_saved: "Employee saved",
  hr_toast_checked_in: "Check-in recorded",
  hr_toast_checked_out: "Check-out recorded",
  hr_toast_payroll_created: "Payroll {period} created — {count} employees, total {total}",
  hr_toast_payroll_posted: "Salaries paid and posted to the ledger (salary expense / cash)",

  // halls (page)
  halls_title: "My halls",
  halls_add: "Add hall",
  hall_status_draft: "Draft",
  hall_status_pending_review: "Pending review",
  hall_status_published: "Published",
  hall_status_suspended: "Suspended",
  halls_capacity: "Capacity {total} people",
  halls_price_from: "· from {price} per guest",
  halls_rating: "★ {avg} ({count} reviews)",
  hall_edit: "Edit",
  hall_uploading: "Uploading…",
  hall_add_photo: "Photo",
  hall_photos: "Photos",
  hall_add_menu: "Menu",
  hall_submit_publish: "Submit for publishing",
  hall_price_suggestion: "Price suggestion",
  halls_empty: "You haven't added any halls yet — start with the “Add hall” button.",
  halls_toast_photo_too_big: "Photo must be under 15 MB",
  halls_toast_photo_added: "Photo added",
  halls_toast_photo_failed: "Photo upload failed",
  halls_toast_sent_publish: "Sent for review and publishing",
  halls_toast_hall_created:
    "Hall saved — now add its menus, then press “Submit for publishing”",
  halls_toast_hall_saved: "Hall changes saved",
  halls_toast_menu_created: "Menu saved",
  halls_price_dialog_title: "Price suggestion — {hall}",
  halls_price_occupancy: "Occupancy over the next 90 days: {pct}%",
  halls_price_peak: "· busiest day: {day}",
  halls_price_col_menu: "Menu",
  halls_price_col_current: "Current price (per guest)",
  halls_price_col_suggested: "Suggested",
  halls_price_footnote: "Suggestions are advisory — a price change is always yours to confirm.",

  // hall types
  hall_type_luxury: "Luxury",
  hall_type_standard: "Standard",
  hall_type_garden: "Garden",
  hall_type_vip_salon: "VIP salon",

  // amenities
  amenity_parking: "Parking",
  amenity_generator: "Generator",
  amenity_bridal_room: "Bridal room",
  amenity_vip_room: "VIP room",
  amenity_av_system: "Sound system",
  amenity_garden: "Garden",

  // hall form
  hallform_edit_title: "Edit hall",
  hallform_create_title: "Add a new hall",
  hallform_name: "Hall name",
  hallform_city: "City",
  hallform_type: "Hall type",
  hallform_cap_total: "Total capacity",
  hallform_cap_mens: "Men's",
  hallform_cap_womens: "Women's",
  hallform_hall_fee: "Hall fee (AFN, separate from per-guest)",
  hallform_deposit_pct: "Deposit (% of total)",
  hallform_instant: "Instant booking (no approval needed from you; goes straight to the deposit stage)",
  hallform_amenities: "Amenities",
  hallform_cancel_policy:
    "Default cancellation terms: up to 30 days before 100%, up to 7 days before 50% deposit refund — editable later.",
  hallform_save_changes: "Save changes",
  hallform_create_submit: "Add hall",
  hallform_error: "Could not save the hall",

  // menu form
  menuform_title: "New menu (price per guest)",
  menuform_name: "Menu name",
  menuform_name_placeholder: "e.g. Standard menu",
  menuform_price: "Price per guest (AFN)",
  menuform_courses: "Dishes (separate with ،)",
  menuform_courses_placeholder: "Qabili, kebab, salad, soft drink",
  menuform_submit: "Save menu",
  menuform_error: "Could not save the menu",

  // shared (more)
  common_delete: "Delete",
  common_reject: "Reject",
  common_publish: "Publish",

  // hall photos dialog
  photos_load_failed: "Could not load photos",
  photos_delete_failed: "Could not delete the photo",
  photos_title: "Photos of {hall}",
  photos_intro:
    "The first photo is the hall's cover and appears in search results. Delete it and the next photo takes its place.",
  photos_empty: "No photos added yet — without a photo, the hall shows a blank in search results.",
  photos_cover_chip: "Cover photo",
  photos_delete_aria: "Delete photo",
  photos_add: "Add photo",
  photos_confirm_title: "Delete this photo?",
  photos_confirm_cover: "This is the hall's cover photo. Delete it and the next photo takes its place.",
  photos_confirm_generic: "This cannot be undone.",

  // printable receipt
  receipt_print_title: "Payment receipt",
  receipt_print_subtitle: "{purpose} receipt",
  receipt_print_no: "Receipt number",
  receipt_print_issued: "Issued on",
  receipt_print_hall: "Hall",
  receipt_print_customer: "Customer",
  receipt_print_phone: "Phone number",
  receipt_print_event_date: "Event date",
  receipt_print_method: "Payment method",
  receipt_print_amount: "Amount received",
  receipt_print_contract_total: "Contract total",
  receipt_print_total_paid: "Total paid",
  receipt_print_balance: "Balance",
  receipt_print_note: "Note",
  receipt_print_hall_sign: "Hall signature: ____________",
  receipt_print_customer_sign: "Customer signature: ____________",
  receipt_print_footer: "This receipt was issued by the Talar system.",

  // admin
  admin_kpi_published: "Published halls",
  admin_kpi_awaiting: "Pending review",
  admin_kpi_confirmed: "Confirmed bookings",
  admin_kpi_tickets: "Open tickets",
  admin_pending_halls_title: "Halls pending review",
  admin_col_name: "Name",
  admin_col_city: "City",
  admin_col_type: "Type",
  admin_col_capacity: "Capacity",
  admin_approve_publish: "Approve & publish",
  admin_empty_pending: "Nothing pending review",
  admin_settlement_title: "Payment settlement",
  admin_run_settlement: "Run settlement",
  admin_running: "Running…",
  admin_col_org: "Organization",
  admin_col_gross: "Gross",
  admin_col_commission: "Commission",
  admin_col_net: "Net",
  admin_mark_paid: "Mark paid",
  admin_empty_payouts: "Press “Run settlement” to see unsettled payments",
  admin_reviews_title: "Reviews pending approval",
  admin_col_hall: "Hall",
  admin_col_rating: "Rating",
  admin_col_text: "Text",
  admin_col_date: "Date",
  admin_empty_reviews: "No reviews pending approval",

  // sign-up / password reset / auth errors (LoginPage)
  login_no_account: "No account yet?",
  login_create_account: "Create a hall account",
  login_have_account: "Already have an account? Sign in",
  login_forgot: "Forgot your password?",
  signup_title: "Create an account for your hall",
  signup_subtitle:
    "It's free. After creating the account you'll register your hall or company name.",
  signup_name: "Full name",
  signup_password_repeat: "Repeat password",
  signup_submit: "Create account",
  signup_submitting: "Creating account…",
  signup_password_short: "Password must be at least {n} characters",
  signup_password_mismatch: "Password and its repeat do not match",
  forgot_need_email: "Enter your email above first",
  forgot_sent:
    "If this email has a Talar account, a password-reset link has been sent. Check your inbox (and Spam).",
  auth_err_invalid_credential: "Email or password is incorrect",
  auth_err_email_in_use:
    "An account already exists with this email — sign in or reset the password",
  auth_err_invalid_email: "The email address is not valid",
  auth_err_weak: "Password is too simple — mix letters and digits",
  auth_err_disabled: "This account is disabled — contact Talar support",
  auth_err_generic: "Sign-in failed — try again",

  // calendar (Jalali month grid)
  calendar_today: "Today",
  calendar_prev_month: "Previous month",
  calendar_next_month: "Next month",
  calendar_legend_free: "Free",
  calendar_legend_partial: "One shift booked",
  calendar_legend_full: "Full",
  calendar_month_summary: "{count} bookings this month",
  weekday_sat: "Sat",
  weekday_sun: "Sun",
  weekday_mon: "Mon",
  weekday_tue: "Tue",
  weekday_wed: "Wed",
  weekday_thu: "Thu",
  weekday_fri: "Fri",

  // dashboard — getting started (new hall owner)
  start_title: "Getting started — to your first booking",
  start_step_hall: "Register your hall: name, city, capacity and fee",
  start_step_menu: "Add menus with per-guest prices, and upload photos",
  start_step_publish:
    "Submit for publishing — once approved, customers see and book your hall in the app",
  start_pending: "Your hall is awaiting review — once approved it appears in the customer app.",
  start_go_halls: "Go to halls",

  // finance — account types
  acct_type_asset: "Asset",
  acct_type_liability: "Liability",
  acct_type_equity: "Equity",
  acct_type_income: "Income",
  acct_type_expense: "Expense",

  // confirmations + accessible labels (UI/UX pass)
  common_confirm: "Confirm",
  cp_show_password: "Show password",
  cp_hide_password: "Hide password",
  confirm_ledger_note: "Ledger postings cannot be edited; a correction is a reversing entry.",
  confirm_verify_title: "Post this receipt to the ledger?",
  confirm_verify_body: "Receipt {no} for {amount} will be verified and posted to the ledger.",
  confirm_payroll_title: "Pay and post this payroll?",
  confirm_payroll_body: "Payroll {period} totalling {total} will be recorded as paid and posted to the ledger.",
  confirm_paid_title: "Mark this payout as paid?",
  confirm_paid_body: "The {amount} payout to organization {org} will be marked paid. Confirm only once the money has actually been transferred.",
  confirm_settle_title: "Run settlement?",
  confirm_settle_body: "Payouts will be created for every verified online payment not yet settled.",
  confirm_reject_hall_title: "Reject this hall?",
  confirm_reject_hall_body: "“{name}” will not be published.",
  confirm_reject_review_title: "Reject this review?",
  confirm_reject_review_body: "This review will not be published.",
};
